import { useState } from 'react';
import { Notice } from '../../components/ui/Notice';
import {
  VERSION_DESCRIPTION_MAX,
  VERSION_NAME_MAX,
  blankVersionDraft,
  versionDraftProblem,
  type VersionDraft,
} from './version-form';

/**
 * CREATE A VERSION — PH-28's form.
 *
 * 🔴 THE TICKET: `features/versions/` contained zero POSTs, so `/versions?project=16` was a read-only
 * page over an API that has had the whole lifecycle for ages. This is the first half of the fix.
 *
 * 🔴 MOBILE FIRST, 390px — the same SHAPE as `CompleteSprintDialog`, `SprintFormDialog` and
 * `PromoteDialog`, deliberately rather than a new one: a BOTTOM SHEET on a phone (`items-end`,
 * rounded top corners, full width) and a centred dialog from `sm:` up. Every field is 44px tall and
 * the buttons stack full-width below `sm:` with the primary one nearest the thumb. ⛔ A centred card
 * at 390px puts the confirm button in the middle of the screen, which is where nobody's thumb is.
 *
 * ⭐ THE DRAFT LIVES HERE, not in the hook — the division `SprintFormDialog` makes. The hook owns the
 * WRITE and its refusal; this owns what is on the form until Save is pressed. That is why a failed
 * save leaves every field exactly as typed.
 *
 * ⛔ SUBMIT VALIDATES AND CAN WRITE NOTHING, and the button is deliberately NOT disabled to achieve
 * that. A greyed-out Save with no explanation is the "it did nothing" failure this codebase refuses;
 * a pressed Save that answers `A version needs a name.` says what to do next — and the guard is then
 * reachable through the UI, which is the only kind a test can prove.
 */

const FIELD_CLASS =
  'mt-1 h-11 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 text-sm disabled:opacity-50';
const LABEL_CLASS = 'block text-xs font-semibold';

export function VersionFormDialog({
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  /** The API's own words for a refusal. */
  error: string | null;
  onCancel: () => void;
  onSubmit: (draft: VersionDraft) => void;
}) {
  // ⭐ Initialised ONCE per open — the page renders this only while the form is open, so there is no
  //   stale draft to reset and no effect to write one.
  const [draft, setDraft] = useState<VersionDraft>(blankVersionDraft);
  const [problem, setProblem] = useState<string | null>(null);

  const set = <K extends keyof VersionDraft>(key: K, value: VersionDraft[K]) => {
    // Immutable, and it clears our own complaint: a message about a field the user has since fixed
    // reads as a fresh refusal.
    setDraft((current) => ({ ...current, [key]: value }));
    setProblem(null);
  };

  const submit = () => {
    const found = versionDraftProblem(draft);
    setProblem(found);
    // ⛔ THE ASSERTION THIS EXISTS FOR: an invalid draft writes NOTHING. It does not go to the API to
    //    come back as the generic `the request body or query was not valid`.
    if (found) return;
    onSubmit(draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="New version"
        className="max-h-full w-full max-w-lg space-y-3 overflow-y-auto rounded-t-lg border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4 sm:rounded-lg"
      >
        <h2 className="text-base font-semibold">New version</h2>

        {/* ⭐ The same statement `SprintFormDialog` makes for `planned`: the API gives no choice of
            state, so what is about to be created is a fact worth saying rather than a hint. */}
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          A new version is created <strong>Unreleased</strong>. It becomes a release only when you
          press <strong>Release</strong> on it — and releasing cannot be undone.
        </p>

        <div>
          <label className={LABEL_CLASS} htmlFor="version-form-name">
            Name
          </label>
          <input
            id="version-form-name"
            type="text"
            value={draft.name}
            maxLength={VERSION_NAME_MAX}
            onChange={(event) => set('name', event.target.value)}
            disabled={busy}
            placeholder="v1.0.35"
            className={FIELD_CLASS}
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Unique within this project — the API answers a duplicate with{' '}
            <em>a version named '…' already exists in this project</em>.
          </p>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="version-form-release-date">
            Planned release date{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
          </label>
          {/* 🔴 `type="date"`, holding the API's own `YYYY-MM-DD`. ⛔ No `new Date()` is constructed
              between this field and the request body, and `formatCalendarDate` must never be used to
              fill it: `1 Sept 2026` is a value the input cannot hold, so it would show EMPTY. */}
          <input
            id="version-form-release-date"
            type="date"
            value={draft.releaseDate}
            onChange={(event) => set('releaseDate', event.target.value)}
            disabled={busy}
            className={FIELD_CLASS}
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            ⚠️ A <strong>plan</strong>, not a release. Setting it here does not release anything, and
            Release stamps the day it actually shipped over the top of it.
          </p>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="version-form-description">
            Description{' '}
            <span className="font-normal text-[color:var(--color-ink-muted)]">(optional)</span>
          </label>
          <textarea
            id="version-form-description"
            value={draft.description}
            maxLength={VERSION_DESCRIPTION_MAX}
            onChange={(event) => set('description', event.target.value)}
            disabled={busy}
            rows={2}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm disabled:opacity-50"
          />
        </div>

        {/* ⚠️ TWO SOURCES OF "NO", KEPT APART. `problem` is OUR pre-flight refusal and nothing was
            sent; `error` is the API's and something was. A reader who cannot tell them apart cannot
            tell whether pressing Save again might work. */}
        {problem ? (
          <Notice tone="warning" title="This version was not sent">
            <p className="break-words">{problem}</p>
          </Notice>
        ) : null}

        {error ? (
          <Notice tone="error" title="The API refused this version">
            <p className="break-words">{error}</p>
          </Notice>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm disabled:opacity-50 sm:h-9"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-11 rounded bg-[color:var(--color-accent)] px-4 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50 sm:h-9"
          >
            {busy ? 'Creating…' : 'Create version'}
          </button>
        </div>
      </div>
    </div>
  );
}
